<template>
    <div id="chart-settings-wrapper">
        <!-- Info (polite) -->
        <v-snackbar top :color="info.color" v-model="info.show" role="status" aria-live="polite" aria-atomic="true">
            <v-icon aria-hidden="true">{{ info.icon }}</v-icon>
            <span class="font-weight-bold pl-2">{{ info.message }}</span>
        </v-snackbar>

        <!-- Accessible activator button (replaces v-card link) -->

        <v-menu v-model="menu" :close-on-content-click="false" bottom content-class="v-settings" left nudge-left="8" offset-x origin="top right" transition="scale-transition">
            <!-- ✅ Richtiger Activator: Menu ankert wieder korrekt am Button -->
            <template v-slot:activator="{ on, attrs }">
                <div
                    id="chart-settings-activator"
                    class="clipboard-trigger chart-settings-pos"
                    role="button"
                    tabindex="0"
                    aria-haspopup="dialog"
                    :aria-expanded="menu ? 'true' : 'false'"
                    aria-controls="chart-settings-menu"
                    aria-label="Open chart settings"
                    @click.stop="menu = true"
                    @keydown.enter.prevent.stop="menu = true"
                    @keydown.space.prevent.stop="menu = true"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon aria-hidden="true" color="white" size="36px">mdi-chart-timeline-variant</v-icon>
                    <span class="sr-only">Open chart settings</span>
                </div>
            </template>

            <v-card
                v-focus-trap="menu"
                class="text-center mb-0"
                width="320"
                id="chart-settings-menu"
                role="dialog"
                aria-modal="true"
                aria-labelledby="chart-settings-title"
                aria-describedby="chart-settings-desc"
                tabindex="-1"
                ref="menuCard">
                <p id="chart-settings-desc" class="sr-only">Use Tab to navigate the settings. Press Escape to return focus to the chart settings button. The menu remains open.</p>
                <v-card-text class="pa-0">
                    <strong id="chart-settings-title" class="d-inline-block sr-only">Chart settings</strong>

                    <v-expansion-panels accordion>
                        <!-- Line chart -->
                        <v-expansion-panel>
                            <v-expansion-panel-header expand-icon="mdi-chevron-down" data-focus-first>
                                <div class="panel-header">
                                    <v-avatar color="rgb(236 236 236)" class="mr-3">
                                        <v-icon color="primary" class="mt-auto mb-auto" aria-hidden="true">mdi-chart-timeline-variant</v-icon>
                                    </v-avatar>
                                    <span class="mt-auto mb-auto panel-title" id="line-chart-section">Line chart</span>
                                </div>
                            </v-expansion-panel-header>

                            <v-expansion-panel-content aria-labelledby="line-chart-section">
                                <v-row align="center">
                                    <v-col cols="6" class="py-0">
                                        <v-switch
                                            v-if="!$store.state.user_settings.advancedMode"
                                            v-model="localLineChartVisible"
                                            @change="$emit('toggleLineChart')"
                                            class="ma-0 pa-0"
                                            :label="lineChartVisible ? 'Active' : 'Hidden'"
                                            hide-details
                                            aria-label="Toggle line chart visibility" />
                                    </v-col>
                                    <v-col cols="6" class="py-0 d-flex justify-end">
                                        <!-- Settings -->
                                        <v-btn
                                            ref="btnLineSettings"
                                            class="ml-auto"
                                            icon
                                            @click="onLineSettingsClick"
                                            @keydown.enter.prevent="onLineSettingsClick"
                                            @keydown.space.prevent="onLineSettingsClick"
                                            :aria-label="'Open line chart settings'"
                                            title="Open line chart settings">
                                            <v-icon aria-hidden="true">mdi-cog</v-icon>
                                        </v-btn>
                                        <!-- Download -->
                                        <v-btn
                                            ref="btnLineDownload"
                                            class="ml-2"
                                            icon
                                            @click="onLineDownloadClick"
                                            @keydown.enter.prevent="onLineDownloadClick"
                                            @keydown.space.prevent="onLineDownloadClick"
                                            :aria-label="'Download line chart'"
                                            title="Download line chart">
                                            <v-icon aria-hidden="true">mdi-cloud-download</v-icon>
                                        </v-btn>

                                        <!-- Reset -->
                                        <v-btn
                                            ref="btnLineReset"
                                            class="ml-2"
                                            icon
                                            @click="onLineResetClick"
                                            @keydown.enter.prevent="onLineResetClick"
                                            @keydown.space.prevent="onLineResetClick"
                                            :aria-label="'Reset line chart'"
                                            title="Reset line chart">
                                            <v-icon aria-hidden="true">mdi-refresh</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>

                        <!-- Pie chart -->
                        <v-expansion-panel v-if="!$store.state.user_settings.advancedMode">
                            <v-expansion-panel-header expand-icon="mdi-chevron-down">
                                <div class="panel-header">
                                    <v-avatar color="rgb(236 236 236)" class="mr-3">
                                        <v-icon color="primary" class="mt-auto mb-auto" aria-hidden="true">mdi-chart-pie</v-icon>
                                    </v-avatar>
                                    <span class="mt-auto mb-auto panel-title" id="pie-chart-section">Pie chart</span>
                                </div>
                            </v-expansion-panel-header>

                            <v-expansion-panel-content aria-labelledby="pie-chart-section">
                                <v-row class="pb-3">
                                    <v-col cols="12" class="py-0">
                                        <v-select
                                            rounded
                                            filled
                                            hide-details
                                            small-chips
                                            multiple
                                            chips
                                            deletable-chips
                                            return-object
                                            :items="chartHeaders"
                                            v-model="selectedPieHeaders"
                                            label="Visible datasets"
                                            aria-label="Select visible datasets for pie chart"
                                            @input="togglePieChart(true)" />
                                    </v-col>
                                </v-row>

                                <v-row align="center">
                                    <v-col cols="6" class="py-0">
                                        <v-switch
                                            v-model="localPieChartVisible"
                                            @change="togglePieChart(false)"
                                            class="ma-0 pa-0"
                                            :label="pieChartVisible ? 'Active' : 'Hidden'"
                                            hide-details
                                            aria-label="Toggle pie chart visibility" />
                                    </v-col>
                                    <v-col cols="6" class="py-0 d-flex justify-end">
                                        <v-btn class="ml-auto" icon @click="$emit('resetPieChart')" :aria-label="'Reset pie chart'" title="Reset pie chart">
                                            <v-icon aria-hidden="true">mdi-refresh</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
    export default {
        name: "ChartSettings",
        props: {
            lineChartVisible: Boolean,
            pieChartVisible: Boolean
        },
        data: () => ({
            localPieChartVisible: null,
            localLineChartVisible: null,
            menu: false,
            lastFocusedEl: null,
            focusTimer: null,
            info: {
                message: null,
                icon: null,
                color: null,
                show: false
            },
            nextRestoreRef: null
        }),
        computed: {
            headers: {
                get() {
                    return this.$store.state.header_items
                },
                set(newValue) {
                    this.$store.dispatch("CHANGE_HEADER_ITEMS", { items: newValue })
                    this.$emit("pieHeadersChanges", this.selectedPieHeaders)
                }
            },
            chartHeaders() {
                if (this.headers && this.headers.modifiedHeaders) {
                    return this.headers.modifiedHeaders.filter((header) => (header.custom && header.customOptions.type == "Dropdown") || header.value == "cats")
                }
                return []
            },
            selectedLineHeaders: {
                get() {
                    if (this.headers && this.headers.modifiedHeaders) {
                        return this.headers.modifiedHeaders.filter((header) => {
                            if (header.chart_options && header.chart_options.line_chart) {
                                return header.chart_options.line_chart.status.active === true
                            }
                            return false
                        })
                    } else {
                        return []
                    }
                },
                set(newHeaders) {
                    this.headers.modifiedHeaders.map((header) => {
                        if (header.chart_options && header.chart_options.line_chart) {
                            header.chart_options.line_chart.status.active = false
                        } else {
                            if (header.chart_options) {
                                header.chart_options.line_chart = { status: { active: false } }
                            } else {
                                header.chart_options = { line_chart: { status: { active: false } } }
                            }
                        }
                    })
                    newHeaders.map((newHeader) => {
                        const index = this.headers.modifiedHeaders.findIndex((h) => h.value == newHeader.value)
                        if (index > -1) {
                            if (this.headers.modifiedHeaders[index].chart_options && this.headers.modifiedHeaders[index].chart_options.line_chart) {
                                this.headers.modifiedHeaders[index].chart_options.line_chart.status.active = true
                            } else {
                                if (this.headers.modifiedHeaders[index].chart_options) {
                                    this.headers.modifiedHeaders[index].chart_options.line_chart = { status: { active: true } }
                                } else {
                                    this.headers.modifiedHeaders[index].chart_options = { line_chart: { status: { active: true } } }
                                }
                            }
                        }
                    })
                    this.$store.dispatch("CHANGE_HEADER_ITEMS", { items: this.headers })
                    this.$emit("lineHeadersChanges", this.selectedLineHeaders)
                }
            },
            selectedPieHeaders: {
                get() {
                    if (this.headers && this.headers.modifiedHeaders) {
                        return this.headers.modifiedHeaders.filter((header) => {
                            if (header.chart_options && header.chart_options.pie_chart) {
                                return header.chart_options.pie_chart.status.active === true
                            }
                            return false
                        })
                    } else {
                        return []
                    }
                },
                set(newHeaders) {
                    this.headers.modifiedHeaders.map((header) => {
                        if (header.chart_options && header.chart_options.pie_chart) {
                            header.chart_options.pie_chart.status.active = false
                        } else {
                            if (header.chart_options) {
                                header.chart_options.pie_chart = { status: { active: false } }
                            } else {
                                header.chart_options = { pie_chart: { status: { active: false } } }
                            }
                        }
                    })
                    newHeaders.map((newHeader) => {
                        const index = this.headers.modifiedHeaders.findIndex((h) => h.value == newHeader.value)
                        if (index > -1) {
                            if (this.headers.modifiedHeaders[index].chart_options && this.headers.modifiedHeaders[index].chart_options.pie_chart) {
                                this.headers.modifiedHeaders[index].chart_options.pie_chart.status.active = true
                            } else {
                                if (this.headers.modifiedHeaders[index].chart_options) {
                                    this.headers.modifiedHeaders[index].chart_options.pie_chart = { status: { active: true } }
                                } else {
                                    this.headers.modifiedHeaders[index].chart_options = { pie_chart: { status: { active: true } } }
                                }
                            }
                        }
                    })
                    this.$store.dispatch("CHANGE_HEADER_ITEMS", { items: this.headers })
                    this.$emit("pieHeadersChanges", this.selectedPieHeaders)
                }
            }
        },
        watch: {
            pieChartVisible(val) {
                this.localPieChartVisible = val
            },
            lineChartVisible(val) {
                this.localLineChartVisible = val
            }
        },
        methods: {
            focusRef(refName, attempt = 0) {
                const MAX = 12 // ~12 Frames ≈ 200ms
                const ref = this.$refs[refName]
                const el = ref && (ref.$el || ref)
                if (!el) {
                    if (attempt < MAX) return requestAnimationFrame(() => this.focusRef(refName, attempt + 1))
                    return false
                }
                // nur focussieren, wenn sichtbar & im Layout
                const visible = !!(el.offsetParent || el.getClientRects().length)
                if (!visible) {
                    if (attempt < MAX) return requestAnimationFrame(() => this.focusRef(refName, attempt + 1))
                    return false
                }
                // sicherstellen, dass fokusierbar
                if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0")
                try {
                    el.focus({ preventScroll: true })
                    return true
                } catch (e) {
                    /* noop */
                }
                if (attempt < MAX) return requestAnimationFrame(() => this.focusRef(refName, attempt + 1))
                return false
            },
            onEscMenu() {
                // Menü bleibt offen, Fokus zurück zum Auslöser/letzten sinnvollen Button
                this.restoreFocus()
            },
            onLineSettingsClick() {
                // Nach Schließen des Dialogs zurück zum Zahnrad-Button
                this.nextRestoreRef = "btnLineSettings"
                // Fokus-Quelle festhalten (optional)
                this.lastFocusedEl = (this.$refs.btnLineSettings && this.$refs.btnLineSettings.$el) || document.activeElement || null
                // Dialog im Kind öffnen
                this.$emit("settingsLineChart")
            },

            onLineDownloadClick() {
                this.nextRestoreRef = "btnLineDownload"
                this.lastFocusedEl = (this.$refs.btnLineDownload && this.$refs.btnLineDownload.$el) || document.activeElement || null
                // Menü bleibt offen; nur Dialog triggern
                this.$emit("downloadLineChart")
            },

            onLineResetClick() {
                this.nextRestoreRef = "btnLineSettings" // optional
                this.$emit("resetLineChart")
            },
            openLineSettings() {
                // Menü bleibt offen
                this.nextRestoreRef = "btnLineSettings"
                this.lastFocusedEl = null
                this.$nextTick(() => {
                    this.$emit("settingsLineChart")
                })
            },

            openLineDownload() {
                // Nach ESC zurück zum Download-Button
                this.nextRestoreRef = "btnLineDownload"
                this.lastFocusedEl = null
                // Menü bleibt offen
                this.$nextTick(() => {
                    this.$emit("downloadLineChart")
                })
            },

            openLineReset() {
                // Nach ESC zurück zum Reset-Button
                this.nextRestoreRef = "btnLineReset"
                this.lastFocusedEl = null
                // Menü bleibt offen
                this.$nextTick(() => {
                    this.$emit("resetLineChart")
                })
            },
            // ---- Accessibility / Focus handling
            captureFocus() {
                this.lastFocusedEl = document.activeElement || null
            },
            restoreFocus() {
                this.$nextTick(() => {
                    // 1) Ziel-Ref (Download/Settings/Reset) bevorzugen – mit Retry bis sichtbar
                    if (this.nextRestoreRef) {
                        const ok = this.focusRef(this.nextRestoreRef, 0)
                        if (ok) {
                            this.nextRestoreRef = null
                            return
                        } else {
                            // Wenn Helper false zurückgab, trotzdem aufräumen und weiter zu den Fallbacks
                            this.nextRestoreRef = null
                        }
                    }

                    // 2) zuletzt fokussiertes Element
                    if (this.lastFocusedEl && typeof this.lastFocusedEl.focus === "function") {
                        try {
                            this.lastFocusedEl.focus({ preventScroll: true })
                        } catch (e) {}
                        this.lastFocusedEl = null
                        return
                    }

                    // 3) Fallback: runder Chart-Settings-Activator
                    const fallback = document.getElementById("chart-settings-activator")
                    if (fallback && typeof fallback.focus === "function") {
                        try {
                            fallback.focus({ preventScroll: true })
                        } catch (e) {}
                    }
                })
            },

            onMenuToggle(val) {
                if (this.focusTimer) {
                    clearTimeout(this.focusTimer)
                    this.focusTimer = null
                }
                if (val) {
                    // beim Öffnen: nach der Transition in den Menüinhalt fokussieren
                    this.$nextTick(() => {
                        this.focusTimer = setTimeout(() => {
                            this.focusMenu()
                        }, 180)
                    })
                } else {
                    // nur dann Fokus zurück zum Activator, wenn wir NICHT gezielt in einen Dialog wechseln
                    if (this.lastFocusedEl) {
                        this.restoreFocus()
                    }
                }
            },

            focusMenu(attempt = 0) {
                if (!this.menu) return
                this.$nextTick(() => {
                    const panel = this.$refs.menuCard && (this.$refs.menuCard.$el || this.$refs.menuCard)
                    const firstFocusable =
                        panel &&
                        panel.querySelector(`
                  input, button, [href], select, textarea,
                  .v-input__slot [tabindex="0"], [tabindex]:not([tabindex="-1"])
                `)
                    if (firstFocusable && typeof firstFocusable.focus === "function") {
                        firstFocusable.focus()
                        return
                    }
                    if (attempt < 10) {
                        requestAnimationFrame(() => this.focusMenu(attempt + 1))
                    } else {
                        if (panel && typeof panel.focus === "function") panel.focus()
                    }
                })
            },

            // ---- Existing logic
            toggleLineChart(forcedShow = false) {
                if (forcedShow && this.localPieChartVisible == false) {
                    this.$emit("togglePieChart")
                } else if (!forcedShow) {
                    let lineHeaders = this.headers.modifiedHeaders.filter((header) => (header.custom && header.customOptions.type == "Dropdown") || header.value == "cats")
                    lineHeaders = lineHeaders.filter((header) => {
                        if (header.chart_options && header.chart_options.line_chart) {
                            return header.chart_options.line_chart.status.active === true
                        }
                        return false
                    })
                    if (lineHeaders.length === 0) {
                        const notification = { message: "There are no columns activated for the chart. Please add a dataset!", icon: "mdi-alert", color: "warning" }
                        this.$emit("createNotification", notification)
                        this.localLineChartVisible = false
                    } else {
                        this.$emit("toggleLineChart")
                    }
                }
            },
            togglePieChart(forcedShow = false) {
                if (forcedShow && this.localPieChartVisible == false) {
                    this.$emit("togglePieChart")
                } else if (!forcedShow) {
                    let pieHeaders = this.headers.modifiedHeaders.filter((header) => (header.custom && header.customOptions.type == "Dropdown") || header.value == "cats")
                    pieHeaders = pieHeaders.filter((header) => {
                        if (header.chart_options && header.chart_options.pie_chart) {
                            return header.chart_options.pie_chart.status.active === true
                        }
                        return false
                    })
                    if (pieHeaders.length === 0) {
                        const notification = { message: "There are no columns activated for the chart. Please add a dataset!", icon: "mdi-alert", color: "warning" }
                        this.$emit("createNotification", notification)
                        this.localPieChartVisible = false
                    } else {
                        this.$emit("togglePieChart")
                    }
                }
            },
            createInfo(message, color, icon) {
                this.info.message = message
                this.info.color = color
                this.info.icon = icon
                this.info.show = true
            }
        },
        mounted() {
            this.localPieChartVisible = this.pieChartVisible
            this.localLineChartVisible = this.lineChartVisible
        }
    }
</script>

<style scoped>
    /* gut sichtbarer, konsistenter Fokus für interaktive Vuetify-Elemente */
    ::v-deep .v-btn:focus-visible,
    ::v-deep .v-list-item:focus-visible,
   
    /* Falls nicht global vorhanden: identisch zu deinen anderen runden Rechteck-FABs */
    .clipboard-trigger {
        z-index: 9999;
        pointer-events: auto;
        height: 52px !important;
        border-top-right-radius: 0px;
        border-bottom-right-radius: 0px;
        border-top-left-radius: 25px;
        border-bottom-left-radius: 25px;
        width: 64px;
        min-width: 56px;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        background: var(--v-primary-base);
        color: #fff;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
        cursor: pointer;

        line-height: 1;
        padding: 0;
        box-sizing: border-box;
    }
    .clipboard-trigger:hover {
        filter: brightness(1.05);
    }
    .clipboard-trigger:active {
        transform: translateY(1px);
    }

    #maintitle {
        padding-top: 3px;
        line-height: 25px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        text-align: center !important;
        font-size: 1em;
    }
    #title {
        padding-top: 16px;
        line-height: 25px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        text-align: center !important;
        color: #212529;
        font-size: 1em;
    }
    .chart-settings-pos {
        position: fixed;
        top: 285px;
        right: 0px;
    }
    .chart-settings-activator:hover {
        filter: brightness(1.05);
    }
    .chart-settings-activator:active {
        transform: translateY(1px);
    }
    .chart-settings-activator:focus-visible {
        outline: 3px solid var(--v-primary-base);
        outline-offset: 2px;
    }
    #chart-settings-activator:focus-visible {
        outline: 3px solid #005fcc;
        outline-offset: 2px;
    }

    .panel-header {
        display: flex;
        align-items: center;
    }

    /* Screen-reader only helper */
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 1px, 1px) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }

    .panel-title {
        font-size: 1.15em;
    }
    #divider {
        border: 1px solid rgb(231, 231, 231);
        border-radius: 1px;
    }
</style>
<style>
    .clipboard-trigger:focus-visible {
        outline: 3px solid var(--v-primary-base) !important;
        outline-offset: 2px;
    }
</style>
<style lang="sass" scoped>
    .v-settings
      .v-item-group > *
        cursor: pointer

      &__item
        border-width: 3px
        border-style: solid
        border-color: transparent !important

        &--active
          border-color: #00cae3 !important
    .infotext
      text-align: left

    .v-data-footer__select
      display: none
</style>
